import { MouseEvent, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';

interface Props {
  url?: string | null;
  alt: string;
  /** Rendered height in px. Width follows the image's own aspect ratio. */
  size?: number;
}

/**
 * An asset image, or an honest placeholder.
 *
 * Three states rather than two, because they mean different things to an operator and collapsing
 * them hides a real problem:
 *
 *  - **no URL** — nothing was delivered. Expected for a movie, whose video never enters VMS.
 *  - **URL that fails to load** — something *was* delivered but cannot be shown: an expired
 *    signature, or a file that has since gone. That is a fault worth seeing, not a blank cell.
 *  - **the image**.
 *
 * A broken `<img>` would otherwise render as the browser's default torn-page glyph, which looks
 * identical to "no artwork" and quietly turns a bug into a shrug.
 */
const AssetThumbnail = ({ url, alt, size = 48 }: Props) => {
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  if (!url || failed) {
    return (
      <Tooltip
        title={!url ? 'Not delivered' : 'Delivered, but could not be loaded'}
      >
        <Box
          sx={{
            width: size,
            height: size,
            display: 'grid',
            placeItems: 'center',
            borderRadius: 1,
            border: '1px dashed',
            borderColor: failed ? 'warning.main' : 'divider',
            color: failed ? 'warning.main' : 'text.disabled',
          }}
        >
          <ImageNotSupportedOutlinedIcon fontSize="small" />
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip title="Click to view full size">
        <Box
          component="img"
          src={url}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          onClick={(event: MouseEvent) => {
            // The row itself navigates to the item, so opening the viewer must not also do that.
            event.stopPropagation();
            setOpen(true);
          }}
          sx={{
            height: size,
            maxWidth: size * 2,
            objectFit: 'contain',
            borderRadius: 1,
            display: 'block',
            backgroundColor: 'action.hover',
            cursor: 'zoom-in',
          }}
        />
      </Tooltip>

      {/*
       * Checking artwork is a real review step — an operator has to see whether the image is the right
       * one, right way up and not stretched. A 40px thumbnail cannot answer that, so the full asset is
       * shown at its own aspect ratio, bounded by the viewport.
       */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle sx={{ pr: 6 }}>
          {alt}
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box
            component="img"
            src={url}
            alt={alt}
            sx={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '75vh',
              objectFit: 'contain',
              margin: '0 auto',
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AssetThumbnail;
